import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Save, Loader2, Star, MessageSquare, ShieldCheck, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { authApi, reviewsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { getRoleLabel, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { StarRating, RatingBadge } from '../../components/StarRating';
import { VerifiedProfileBadge, VerificationStatus } from '../../components/VerifiedBadge';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [ratingInfo, setRatingInfo] = useState({ average: 0, total: 0 });
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    iban: user?.iban || '',
  });

  useEffect(() => {
    if (user?.id) {
      fetchReviews();
    }
  }, [user?.id]);

  const fetchReviews = async () => {
    try {
      const response = await reviewsApi.getUserReviews(user.id);
      setReviews(response.data.reviews || []);
      setRatingInfo({
        average: response.data.average_rating || 0,
        total: response.data.total_reviews || 0,
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await authApi.updateProfile(formData);
      updateUser(formData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="profile-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Mon profil
        </h1>
        <p className="text-slate-600 mt-1">Gérez vos informations personnelles</p>
      </div>

      {/* User Info Card */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="relative">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-3xl font-bold text-white">{user?.name?.charAt(0).toUpperCase()}</span>
              </div>
              {user?.verification_status === 'verified' && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                  <ShieldCheck className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900">{user?.name}</h2>
              <p className="text-slate-600">{user?.email}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full">
                  {getRoleLabel(user?.role)}
                </span>
                <VerificationStatus status={user?.verification_status || 'not_started'} />
                {ratingInfo.total > 0 && (
                  <div className="flex items-center gap-2">
                    <StarRating rating={ratingInfo.average} size="sm" />
                    <span className="text-sm text-slate-600">
                      {ratingInfo.average.toFixed(1)} ({ratingInfo.total} avis)
                    </span>
                  </div>
                )}
              </div>
              {user?.verification_status !== 'verified' && (
                <div className="mt-4">
                  <Link to="/dashboard/verify-identity">
                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Vérifier mon identité
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rating Summary */}
      {ratingInfo.total > 0 && (
        <Card className="border-slate-200 bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-5xl font-bold text-yellow-600">{ratingInfo.average.toFixed(1)}</p>
                <StarRating rating={ratingInfo.average} size="md" className="justify-center mt-2" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-medium text-slate-900">Note moyenne</p>
                <p className="text-slate-600">Basée sur {ratingInfo.total} avis</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              Avis reçus
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {reviews.map((review) => (
              <div key={review.id} className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-600">
                        {review.reviewer_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium text-slate-900">{review.reviewer_name}</span>
                  </div>
                  <StarRating rating={review.rating} size="sm" />
                </div>
                {review.comment && (
                  <p className="text-slate-600 mt-2">{review.comment}</p>
                )}
                <p className="text-xs text-slate-400 mt-3">{formatDate(review.created_at)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No Reviews */}
      {reviews.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun avis reçu pour le moment</p>
            <p className="text-sm text-slate-400 mt-1">
              Les avis apparaîtront ici après vos premières livraisons
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Form */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase">
            Modifier mes informations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={user?.email}
                disabled
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-500">L&apos;email ne peut pas être modifié</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                Nom complet
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                Téléphone
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="phone-input"
              />
            </div>

            {/* IBAN field - only for carriers */}
            {user?.role === 'carrier' && (
              <div className="space-y-2">
                <Label htmlFor="iban" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  IBAN (pour les versements)
                </Label>
                <Input
                  id="iban"
                  type="text"
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  data-testid="iban-input"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500">
                  Votre IBAN sera utilisé pour recevoir vos versements après chaque livraison
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
